














































































import { Watch, Component, Vue } from 'vue-property-decorator'
import { SearchAlternatives } from '@/store/referrals'
import { Referral } from '@/api/model/referral'
import { isArray } from 'lodash'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { CompanyAPI } from '@/api/backend-client'
import { namespace } from 'vuex-class'
import { PipelinePhase } from '@/api/model/pipeline-phase'
import { Pipeline } from '@/api/model/pipeline'
import { ReferralStatus } from '@/api/types'
import StatusIcon from '@/components/ui/StatusIcon.vue'
import { StatusReason } from '@/api/model/status-reason'
import { Source } from '@/api/model/source'
const referrals = namespace('referrals')

interface SearchAlternativeViewConfig {
    pipeline: Pipeline,
    phase: PipelinePhase,
    status: ReferralStatus,
}

@Component({
    components: {StatusIcon},
})
export default class NoResults extends Vue {
    @referrals.State readonly loadingAlternatives!: boolean
    @referrals.State readonly view!: string
    @referrals.State readonly pipelineFilter!: string
    @referrals.State readonly phaseFilter!: PipelinePhase
    @referrals.State readonly phaseCategoryFilter!: string
    @referrals.State readonly direction!: string
    @referrals.State readonly statusFilter!: string
    @referrals.State readonly newSourceFilter!: Source[]
    @referrals.State readonly newStatusReasonFilter!: StatusReason[]
    @referrals.State readonly typeFilter!: string[]
    @referrals.State readonly search!: string|null
    @referrals.State readonly searchAlternatives!: SearchAlternatives
    @referrals.Getter readonly requestParams!: string[]
    @referrals.Getter readonly resultsInOtherPipeline!: Array<{ title: string, count: number; filters: {} }>
    @referrals.Getter readonly resultsInOtherPhase!: Array<{ title: string, count: number; filters: {} }>
    @referrals.Getter readonly resultsInOtherStatus!: Array<{ title: string, count: number; filters: {} }>
    @referrals.Getter readonly selectedPipeline!: Pipeline|null
    @referrals.Action applyFilters: (ReferralsStateFilters) => Promise<void>
    availableOutgoingBranches: Branch[] = []
    availableOutgoingUsers: User[] = []
    activeFilters: Array<{ name: string, value: string }> = []

    async mounted(): Promise<void> {
        await this.loadOutgoingBranches()
        await this.loadOutgoingUsers()
        this.updateActiveFilters()
    }

    async loadOutgoingBranches(): Promise<void> {
        this.availableOutgoingBranches = (await CompanyAPI.getOutgoingReferralBranches())
            .sort((a: Branch, b: Branch) => a.name.localeCompare(b.name))
    }

    async loadOutgoingUsers(): Promise<void> {
        const availableUsers = new Map<string, User>()

        this.availableBranches.forEach(branch => {
            branch.company.getAvailableUsers({ branch }).forEach(user => {
                availableUsers.set(user.id, user)
            })
        })

        this.availableOutgoingUsers = Array.from(availableUsers.values())
            .sort((a: User, b: User) => a.name.localeCompare(b.name))
    }

    async setView(view: string, direction: string, pipelineFilter: string, phaseFilter: PipelinePhase, statusFilter: string = 'all') {
        document.querySelector('.main').scrollTo({ top: 0, behavior: 'smooth' })

        if (statusFilter === 'all') {
            statusFilter = null
        }

        await this.applyFilters({
            view,
            direction,
            pipelineFilter,
            phaseFilter,
            statusFilter,
            search: this.search,
            priorityFilter: [],
            statusReasonFilter: [],
            referredByFilter: [],
            referredByBranchFilter: [],
            assignedToFilter: [],
            assignedToBranchFilter: [],
            channelFilter: [],
            newSourceFilter: [],
            scoreFilter: [],
        })
    }

    updateActiveFilters(): void {
        this.activeFilters = []

        for (const [name, value] of this.filterList) {
            if (isArray(value)) {
                for (const v of value) {
                    this.activeFilters.push({
                        name,
                        value: v,
                    })
                }

            } else {
                this.activeFilters.push({
                    name,
                    value,
                })
            }
        }
    }

    renderFilterName(name: string): string {
        switch (name) {
            case 'phase.category': return this.$t(`referral-list-filters.category`).toString()
            default: return this.$t(`referral-list-filters.${name}`).toString()
        }
    }

    renderFilterValue(name: string, value: any): string {
        switch (name) {
            case 'phase.category': return this.$t(`pipeline-phase.category.${value}`).toString()

            case 'status': return this.$t(`referral.status.${value}`).toString()

            case 'priority': return value
                ? this.$t('priority.yes').toString()
                : this.$t('priority.no').toString()

            case 'type': return this.$t(`referral.type.${value}`).toString()

            case 'statusReason': return this.$t(`referral.statusReason.${value}`).toString()

            case 'newStatusReason': return this.newStatusReasonFilter
                .find(statusReason => statusReason.id === value).name

            case 'channel': return this.$t(`referral.channel.${value}`).toString()

            case 'newSource': {
                const source = this.$currentCompany.sources.find(findSource => findSource.id === value)
                return this.$t('referral.channel.' + source.channel).toString() + ' - ' + source.name
            }

            case 'score': {
                switch (value) {
                    case -1: return this.$t('score.low').toString()
                    case  0: return this.$t('score.neutral').toString()
                    case  1: return this.$t('score.high').toString()
                    default: return value
                }
            }

            case 'referredBy':
            case 'assignedTo': {
                const user = [...this.availableUsers, ...this.availableOutgoingUsers].find(v => v.id === value)
                return user ? user.name : value
            }

            case 'referredByBranch':
            case 'assignedToBranch': {
                const branch = [...this.availableBranches, ...this.availableOutgoingBranches].find(v => v.id === value)
                return branch ? branch.name : value
            }

            default: return value
        }
    }

    groupResults(list: Referral[]): Referral[] {
        const results = {}
        for (const referral of list) {
            const key = [
                referral.contact.name,
                referral.contact.address,
                referral.pipeline?.id,
                referral.phase?.id,
            ]
                .filter(v => !!v)
                .join('_')
                .replaceAll('.', '_')
                .replaceAll(',', '_')
                .replaceAll(' ', '')
                .toLowerCase()

            if (!isArray(results[key])) {
                results[key] = []
            }

            results[key].push(referral)
        }

        return Object.values(results)
    }

    get tabTitle() {
        if (this.phaseCategoryFilter) {
            return this.$t(`pipeline-phase.category.${this.phaseCategoryFilter}`).toString()
        }

        if (this.phaseFilter) {
            return this.phaseFilter.name
        }

        if (this.statusFilter) {
            return this.statusLabel
        }

        return 'Alles'
    }

    get availableBranches(): Branch[] {
        return this.availableOutgoingBranches
    }

    get availableUsers(): User[] {
        return this.$currentCompany.getAvailableUsers()
    }

    get viewLabel(): string {
        return this.$t('referral_list.no_result.' + this.view +  '.label').toString()
    }

    get directionLabel(): string {
        return this.$t('referral_list.no_result.' + this.view + '.' + this.direction).toString()
    }

    get statusLabel(): string {
        return this.$t(`referral.status.${this.statusFilter ?? 'all'}`).toString()
    }

    get filterList() {
        return Object.entries(this.requestParams)
            .filter(v => v[0] !== 'view')
            .filter(v => v[0] !== 'page')
            .filter(v => v[0] !== 'status')
            .filter(v => v[0] !== 'range')
            .filter(v => v[0] !== 'pagination')
            .filter(v => v[0] !== 'itemsPerPage')
            .filter(v => v[0] !== 'search')
            .filter(v => v[0] !== 'pipeline')
            .filter(v => v[0] !== 'phase')
            .filter(v => v[0] !== 'phase.category')
            .filter(v => v[0] !== 'category')
            .filter(v => v[0].startsWith('order') === false)
    }

    get hasAlternatives(): boolean {
        return this.hasAlternativesUserIn
            || this.hasAlternativesUserOut
            || this.hasAlternativesTeamIn
            || this.hasAlternativesTeamOut
    }

    get hasAlternativesUserIn(): boolean {
        return this.searchAlternatives.userIn.length > 0
    }

    get hasAlternativesUserOut(): boolean {
        return this.searchAlternatives.userOut.length > 0
    }

    get hasAlternativesTeamIn(): boolean {
        return this.searchAlternatives.teamIn.length > 0
    }

    get hasAlternativesTeamOut(): boolean {
        return this.searchAlternatives.teamOut.length > 0
    }

    get alternativesUserIn(): Referral[] {
        return this.groupResults(this.searchAlternatives.userIn)
    }

    get alternativesUserOut(): Referral[] {
        return this.groupResults(this.searchAlternatives.userOut)
    }

    get alternativesTeamIn(): Referral[] {
        return this.groupResults(this.searchAlternatives.teamIn)
    }

    get alternativesTeamOut(): Referral[] {
        return this.groupResults(this.searchAlternatives.teamOut)
    }

    get alternativesList(): Array<{ key: string, condition: boolean, entries: Referral[], onClick: (config: SearchAlternativeViewConfig) => void }> {
        return [
            {
                key: 'userIn',
                condition: this.hasAlternativesUserIn,
                entries: this.alternativesUserIn,
                onClick: (config: SearchAlternativeViewConfig) =>
                    this.setView('user', 'in', config.pipeline.id, config.phase),
            },
            {
                key: 'userOut',
                condition: this.hasAlternativesUserOut,
                entries: this.alternativesUserOut,
                onClick: (config: SearchAlternativeViewConfig) =>
                    this.setView('user', 'out', config.pipeline.id, config.phase),
            },
            {
                key: 'teamIn',
                condition: this.hasAlternativesTeamIn,
                entries: this.alternativesTeamIn,
                onClick: (config: SearchAlternativeViewConfig) =>
                    this.setView('branches', 'in', config.pipeline.id, config.phase),
            },
            {
                key: 'teamOut',
                condition: this.hasAlternativesTeamOut,
                entries: this.alternativesTeamOut,
                onClick: (config: SearchAlternativeViewConfig) =>
                    this.setView('branches', 'out', config.pipeline.id, config.phase),
            },
        ]
    }
}
